/**
 * Raw React context for the OverlayOrchestrator.
 * Separated so the Provider component and the hook can live in different files
 * (satisfies Vite fast-refresh: component-only files).
 *
 * @internal — consumers should use useOverlayOrchestrator() or OverlayOrchestratorProvider.
 */

import { createContext } from 'react';
import type {
    SurfaceId,
    OverlayKind,
    OverlayEntry,
    OriginEnvelope,
    NavigationContext,
} from './types';

/** The public API shape for the OverlayOrchestrator context */
export interface OverlayOrchestratorAPI {
    stack: readonly OverlayEntry[];
    open(params: {
        surface: SurfaceId;
        overlayKind: OverlayKind;
        origin: OriginEnvelope;
        props?: Record<string, unknown>;
    }): OverlayEntry;
    close(instanceId?: string, returnPayload?: Record<string, unknown>): void;
    /**
     * Close all overlays at and above a given instanceId.
     * Closes top-down (children first). Only the root overlay gets
     * restoration; intermediate overlays are silently removed.
     */
    closeAllAbove(instanceId: string): void;
    closeAll(): void;
    isOpen(surface: SurfaceId): boolean;
    topOverlay: OverlayEntry | null;
    buildOrigin(
        originSurface: SurfaceId,
        triggerElementId?: string,
        extra?: Partial<NavigationContext>,
    ): OriginEnvelope;
}

export const OverlayOrchestratorCtx = createContext<OverlayOrchestratorAPI | null>(null);
